import { useState, useEffect } from 'react'
import { Button, Input, Space, Alert, Tag, Spin, Divider, Typography, theme } from 'antd'
import { CheckCircleOutlined, CloseCircleOutlined, RobotOutlined, SaveOutlined, ThunderboltOutlined } from '@ant-design/icons'
import axiosInstance from '@/lib/axios'

const { Text, Paragraph } = Typography

interface AnthropicStatus {
  configured: boolean
  key_preview: string | null
}

interface TestResult {
  success: boolean
  message: string
}

export const AnthropicSettings = () => {
  const { token } = theme.useToken()
  const [status, setStatus] = useState<AnthropicStatus | null>(null)
  const [loadingStatus, setLoadingStatus] = useState(true)
  const [newKey, setNewKey] = useState('')
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<TestResult | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    loadStatus()
  }, [])

  const loadStatus = async () => {
    try {
      setLoadingStatus(true)
      const response = await axiosInstance.get<AnthropicStatus>('/ai-settings/anthropic')
      setStatus(response.data)
    } catch {
      setStatus({ configured: false, key_preview: null })
    } finally {
      setLoadingStatus(false)
    }
  }

  const handleSave = async () => {
    if (!newKey.trim()) return
    setSaving(true)
    setSaveError(null)
    setTestResult(null)
    try {
      const response = await axiosInstance.post('/ai-settings/anthropic', { api_key: newKey.trim() })
      setStatus({ configured: true, key_preview: response.data.key_preview })
      setNewKey('')
      setTestResult({ success: true, message: 'API key salvata e verificata con successo' })
    } catch (error: any) {
      setSaveError(error.response?.data?.detail || 'Errore durante il salvataggio')
    } finally {
      setSaving(false)
    }
  }

  const handleTest = async () => {
    setTesting(true)
    setTestResult(null)
    try {
      const response = await axiosInstance.post('/ai-settings/anthropic/test')
      setTestResult({ success: response.data.success, message: response.data.message })
    } catch (error: any) {
      setTestResult({ success: false, message: error.response?.data?.detail || 'Errore durante il test' })
    } finally {
      setTesting(false)
    }
  }

  if (loadingStatus) {
    return <div style={{ padding: 32, textAlign: 'center' }}><Spin /></div>
  }

  return (
    <div style={{ maxWidth: 640 }}>
      {/* Stato attuale */}
      <div style={{ marginBottom: 24, padding: 16, background: token.colorFillAlter, borderRadius: 8, border: `1px solid ${token.colorBorderSecondary}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <RobotOutlined style={{ fontSize: 20, color: '#722ed1' }} />
          <Text strong style={{ fontSize: 15 }}>Claude AI (Anthropic)</Text>
          {status?.configured ? (
            <Tag icon={<CheckCircleOutlined />} color="success">Configurata</Tag>
          ) : (
            <Tag icon={<CloseCircleOutlined />} color="error">Non configurata</Tag>
          )}
        </div>

        {status?.configured && status.key_preview && (
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Chiave attiva: </Text>
            <Text code style={{ fontSize: 12 }}>{status.key_preview}</Text>
          </div>
        )}

        <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 10, marginBottom: 0 }}>
          La chiave viene usata per analizzare automaticamente i preventivi fotografati nelle RDO.
          Puoi ottenerla su{' '}
          <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noreferrer">
            console.anthropic.com
          </a>.
        </Paragraph>
      </div>

      {/* Test chiave attuale */}
      {status?.configured && (
        <>
          <div style={{ marginBottom: 20 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Verifica connessione</Text>
            <Button
              icon={<ThunderboltOutlined />}
              onClick={handleTest}
              loading={testing}
              style={{ borderColor: '#722ed1', color: '#722ed1' }}
            >
              Testa chiave attuale
            </Button>
          </div>

          {testResult && (
            <Alert
              type={testResult.success ? 'success' : 'error'}
              message={testResult.message}
              showIcon
              style={{ marginBottom: 20 }}
              closable
              onClose={() => setTestResult(null)}
            />
          )}

          <Divider />
        </>
      )}

      {/* Inserimento / aggiornamento chiave */}
      <div>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          {status?.configured ? 'Aggiorna API key' : 'Configura API key'}
        </Text>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
          Incolla qui la tua chiave Anthropic (formato: <Text code style={{ fontSize: 11 }}>sk-ant-api03-...</Text>).
          La chiave viene salvata nel file <Text code style={{ fontSize: 11 }}>.env</Text> del backend
          ed è attiva immediatamente senza riavvio.
        </Text>

        <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
          <Input.Password
            placeholder="sk-ant-api03-..."
            value={newKey}
            onChange={e => { setNewKey(e.target.value); setSaveError(null) }}
            onPressEnter={handleSave}
            autoComplete="new-password"
            style={{ fontFamily: 'monospace', fontSize: 13 }}
          />
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            disabled={!newKey.trim()}
            style={{ background: '#722ed1', borderColor: '#722ed1' }}
          >
            Salva e Verifica
          </Button>
        </Space.Compact>

        {saveError && (
          <Alert type="error" message={saveError} showIcon closable onClose={() => setSaveError(null)} />
        )}

        {!status?.configured && testResult?.success && (
          <Alert
            type="success"
            message={testResult.message}
            showIcon
            closable
            onClose={() => setTestResult(null)}
          />
        )}
      </div>
    </div>
  )
}
